"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useEffect } from "react";
import { useTheme } from "@/contexts/theme-provider";
import { useUser } from "@/contexts/user-provider";
import { formatStaffName } from "@/lib/format-data";
import { fetchPylonIdentity } from "@/api/user/pylon";
import { usePylonWidgetStore } from "@/stores/pylon-widget-store";
import type { PylonWidgetPosition } from "@/stores/pylon-widget-store";
declare global {
  interface Window {
    Pylon?: ((...args: unknown[]) => void) & {
      q?: unknown[];
      e?: (args: unknown[]) => void;
    };
    pylon?: {
      chat_settings: {
        app_id: string;
        email?: string;
        name?: string;
        avatar_url?: string;
        email_hash?: string;
        account_id?: string;
      };
    };
  }
}
const DRAG_OVERLAY_ID = "pylon-widget-drag-overlay";
const PYLON_SCRIPT_ID = "pylon-widget-script";
const PYLON_WIDGET_Z_INDEX = "40";
const PYLON_DRAG_OVERLAY_Z_INDEX = "44";
const POSTHOG_SESSION_RECORDING_FIELD_SLUG = "posthog_recording_url";
const VIEWPORT_MARGIN = 8;
const MIN_DRAG_DISTANCE = 4;
const FORCE_SESSION_RECORDING_OVERRIDES = {
  sampling: true,
  linked_flag: true,
  url_trigger: true,
  event_trigger: true
} as const;
type ClampRect = Pick<DOMRect, "left" | "right" | "top" | "bottom">;
interface PylonWidgetElements {
  root: HTMLElement;
  bubble: HTMLElement;
  chatWindow: HTMLElement | null;
}
function getAppId(): string {
  if (typeof window === "undefined") return "";
  return window.RUNTIME_ENV?.NEXT_PUBLIC_PYLON_APP_ID ?? process.env.NEXT_PUBLIC_PYLON_APP_ID ?? "";
}
function loadPylonScript(appId: string) {
  if (document.getElementById(PYLON_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = PYLON_SCRIPT_ID;
  script.type = "text/javascript";
  script.async = true;
  script.src = `https://widget.usepylon.com/widget/${appId}`;
  document.head.appendChild(script);
}
function getPylonWidgetElements(): PylonWidgetElements | null {
  const root = document.getElementById("pylon-chat");
  const bubble = document.getElementById("pylon-chat-bubble")?.parentElement;
  const chatWindow = document.querySelector(".PylonChat-chatWindowFrameContainer");
  if (!(root instanceof HTMLElement) || !(bubble instanceof HTMLElement)) {
    return null;
  }
  return {
    root,
    bubble,
    chatWindow: chatWindow instanceof HTMLElement ? chatWindow : null
  };
}
function applyPylonPosition(position: PylonWidgetPosition): PylonWidgetElements | null {
  const elements = getPylonWidgetElements();
  if (!elements) return null;
  const translate = `${Math.round(position.x)}px ${Math.round(position.y)}px`;

  // Do not translate Pylon's root container: it can become the containing block
  // for Pylon's fixed-positioned frames and reset the native widget placement to
  // the top-left of the page. Offset the actual Pylon frames instead.
  elements.root.style.translate = "";
  elements.bubble.style.translate = translate;
  elements.chatWindow?.style.setProperty("translate", translate);
  elements.root.style.setProperty("z-index", PYLON_WIDGET_Z_INDEX, "important");
  return elements;
}
function clampPosition(position: PylonWidgetPosition, deltaX: number, deltaY: number, rect: ClampRect): PylonWidgetPosition {
  const minDeltaX = VIEWPORT_MARGIN - rect.left;
  const maxDeltaX = window.innerWidth - VIEWPORT_MARGIN - rect.right;
  const minDeltaY = VIEWPORT_MARGIN - rect.top;
  const maxDeltaY = window.innerHeight - VIEWPORT_MARGIN - rect.bottom;
  return {
    x: position.x + Math.min(Math.max(deltaX, minDeltaX), maxDeltaX),
    y: position.y + Math.min(Math.max(deltaY, minDeltaY), maxDeltaY)
  };
}
function createDragOverlay() {
  const existingOverlay = document.getElementById(DRAG_OVERLAY_ID);
  if (existingOverlay) return existingOverlay;
  const overlay = document.createElement("div");
  overlay.id = DRAG_OVERLAY_ID;
  overlay.title = "Drag to reposition support chat";
  overlay.setAttribute("aria-hidden", "true");
  Object.assign(overlay.style, {
    position: "fixed",
    cursor: "grab",
    userSelect: "none",
    touchAction: "none",
    display: "none",
    background: "transparent"
  });
  overlay.style.setProperty("z-index", PYLON_DRAG_OVERLAY_Z_INDEX, "important");
  document.body.appendChild(overlay);
  return overlay;
}
function positionDragOverlay(overlay: HTMLElement, root: HTMLElement | null, isChatWindowOpen: boolean) {
  if (!root || isChatWindowOpen) {
    overlay.style.display = "none";
    return;
  }
  const rect = root.getBoundingClientRect();
  Object.assign(overlay.style, {
    display: "block",
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`
  });
}
function addSessionRecordingToPylonIssue() {
  posthog.startSessionRecording(FORCE_SESSION_RECORDING_OVERRIDES);
  window.Pylon?.("setNewIssueCustomFields", {
    [POSTHOG_SESSION_RECORDING_FIELD_SLUG]: posthog.get_session_replay_url({
      withTimestamp: true
    })
  });
}
function temporarilyDisablePointerEvents(element: HTMLElement) {
  const previousPointerEvents = element.style.pointerEvents;
  element.style.pointerEvents = "none";
  return () => {
    element.style.pointerEvents = previousPointerEvents;
  };
}
export default function PylonWidget() {
  const $ = _c(24);
  const {
    currentUser
  } = useUser();
  const {
    resolvedTheme
  } = useTheme();
  const setPosition = usePylonWidgetStore(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getAppId();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const appId = t0;
  const isEligible = !!currentUser?.email && !!appId;
  const t1 = currentUser?.id;
  let t2;
  if ($[1] !== t1) {
    t2 = ["pylon-identity", t1];
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isEligible || $[4] !== t2) {
    t3 = {
      queryKey: t2,
      queryFn: fetchPylonIdentity,
      enabled: isEligible,
      staleTime: Infinity
    };
    $[3] = isEligible;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: identity
  } = useQuery(t3);
  let t4;
  if ($[6] !== currentUser || $[7] !== identity || $[8] !== isEligible) {
    t4 = () => {
      if (!isEligible || !identity) {
        return;
      }
      window.pylon = {
        chat_settings: {
          app_id: appId,
          email: identity.email,
          email_hash: identity.emailHash,
          name: formatStaffName(currentUser),
          avatar_url: currentUser?.profileImage
        }
      };
      loadPylonScript(appId);
    };
    $[6] = currentUser;
    $[7] = identity;
    $[8] = isEligible;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = currentUser?.firstName;
  const t6 = currentUser?.lastName;
  const t7 = currentUser?.profileImage;
  let t8;
  if ($[10] !== identity || $[11] !== isEligible || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = [appId, isEligible, identity, t5, t6, t7];
    $[10] = identity;
    $[11] = isEligible;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  useEffect(t4, t8);
  let t10;
  let t9;
  if ($[16] !== isEligible || $[17] !== resolvedTheme) {
    t9 = () => {
      if (!isEligible) {
        return;
      }
      const applyTheme = () => {
        if (!window.Pylon) {
          return false;
        }
        window.Pylon("setTheme", resolvedTheme);
        return true;
      };
      if (applyTheme()) {
        return;
      }
      const intervalId = window.setInterval(() => {
        if (applyTheme()) {
          window.clearInterval(intervalId);
        }
      }, 250);
      return () => window.clearInterval(intervalId);
    };
    t10 = [isEligible, resolvedTheme];
    $[16] = isEligible;
    $[17] = resolvedTheme;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[20] !== isEligible || $[21] !== setPosition) {
    t11 = () => {
      if (!isEligible) {
        return;
      }
      const overlay = createDragOverlay();
      let widgetElements = null;
      let rafId = null;
      let isChatWindowOpen = false;
      let positionBeforeChatWindowClamp = null;
      let arePylonCallbacksRegistered = false;
      let removePointerListeners = null;
      const scheduleRefresh = () => {
        if (rafId !== null) {
          return;
        }
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          refreshPosition();
        });
      };
      const onShowChatWindow = () => {
        addSessionRecordingToPylonIssue();
        if (!isChatWindowOpen) {
          positionBeforeChatWindowClamp = usePylonWidgetStore.getState().position;
        }
        isChatWindowOpen = true;
        scheduleRefresh();
      };
      const onHideChatWindow = () => {
        isChatWindowOpen = false;
        if (positionBeforeChatWindowClamp) {
          setPosition(positionBeforeChatWindowClamp);
          positionBeforeChatWindowClamp = null;
        }
        scheduleRefresh();
      };
      const registerPylonCallbacks = function registerPylonCallbacks() {
        if (arePylonCallbacksRegistered || !window.Pylon) {
          return;
        }
        window.Pylon("onShow", onShowChatWindow);
        window.Pylon("onHide", onHideChatWindow);
        arePylonCallbacksRegistered = true;
      };
      function refreshPosition() {
        registerPylonCallbacks();
        const currentPosition = usePylonWidgetStore.getState().position;
        widgetElements = applyPylonPosition(currentPosition);
        if (!widgetElements) {
          positionDragOverlay(overlay, null, isChatWindowOpen);
          return;
        }
        const clampElement = isChatWindowOpen && widgetElements.chatWindow ? widgetElements.chatWindow : widgetElements.bubble;
        const clampedPosition = clampPosition(currentPosition, 0, 0, clampElement.getBoundingClientRect());
        if (clampedPosition.x !== currentPosition.x || clampedPosition.y !== currentPosition.y) {
          setPosition(clampedPosition);
          widgetElements = applyPylonPosition(clampedPosition);
        }
        positionDragOverlay(overlay, widgetElements?.bubble ?? null, isChatWindowOpen);
      }
      const onPointerDown = event => {
        if (!widgetElements) {
          return;
        }
        const startPosition = usePylonWidgetStore.getState().position;
        const startRect = widgetElements.bubble.getBoundingClientRect();
        const startPointer = {
          x: event.clientX,
          y: event.clientY
        };
        let hasDragged = false;
        let restorePointerEvents = temporarilyDisablePointerEvents(widgetElements.root);
        event.preventDefault();
        overlay.style.cursor = "grabbing";
        overlay.setPointerCapture(event.pointerId);
        const removeDragListeners = () => {
          window.removeEventListener("pointermove", onPointerMove);
          window.removeEventListener("pointerup", onPointerUp);
          window.removeEventListener("pointercancel", onPointerUp);
          restorePointerEvents();
          restorePointerEvents = _temp2;
        };
        const onPointerMove = moveEvent => {
          const deltaX = moveEvent.clientX - startPointer.x;
          const deltaY = moveEvent.clientY - startPointer.y;
          if (!hasDragged && Math.hypot(deltaX, deltaY) < MIN_DRAG_DISTANCE) {
            return;
          }
          hasDragged = true;
          setPosition(clampPosition(startPosition, deltaX, deltaY, startRect));
        };
        const onPointerUp = upEvent => {
          overlay.style.cursor = "grab";
          if (overlay.hasPointerCapture(upEvent.pointerId)) {
            overlay.releasePointerCapture(upEvent.pointerId);
          }
          if (!hasDragged) {
            onShowChatWindow();
            window.Pylon?.("show");
          }
          removeDragListeners();
          removePointerListeners = null;
        };
        removePointerListeners = removeDragListeners;
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("pointercancel", onPointerUp);
      };
      const observer = new MutationObserver(scheduleRefresh);
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["class", "id", "src", "style"],
        childList: true,
        subtree: true
      });
      registerPylonCallbacks();
      refreshPosition();
      window.Pylon?.("showChatBubble");
      const refreshIntervalId = window.setInterval(scheduleRefresh, 1000);
      const unsubscribe = usePylonWidgetStore.subscribe(scheduleRefresh);
      window.addEventListener("resize", scheduleRefresh);
      overlay.addEventListener("pointerdown", onPointerDown);
      return () => {
        observer.disconnect();
        unsubscribe();
        window.clearInterval(refreshIntervalId);
        window.removeEventListener("resize", scheduleRefresh);
        overlay.removeEventListener("pointerdown", onPointerDown);
        if (arePylonCallbacksRegistered) {
          window.Pylon?.("onShow", null);
          window.Pylon?.("onHide", null);
        }
        overlay.remove();
        removePointerListeners?.();
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
        }
        window.Pylon?.("hide");
        window.Pylon?.("hideChatBubble");
      };
    };
    t12 = [isEligible, setPosition];
    $[20] = isEligible;
    $[21] = setPosition;
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  useEffect(t11, t12);
  return null;
}
function _temp2() {}
function _temp(state) {
  return state.setPosition;
}